import { gsap } from 'gsap';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';


import { CruiseSearchContext } from 'contexts/CruiseSearchContext';

import { Constrain } from 'components/FiltersModal/components';
import Header from 'components/FiltersModal/Header';
import { Container, FilterTitle, Grid, Label, SectionLabel, Wrapper } from './NumberOfGuests.styled';
import { OccupancyState } from './NumberOfGuests.types';

import { ADULTS, ADULTS_DEFAULT, CHILDREN, CHILDREN_DEFAULT, DECREMENT, INCREMENT, OCCUPANCY_RANGE } from 'contexts/CruiseSearchContext/constants';
import { getLabelByBrand } from 'lib/brand';
import OccupancySelector from './OccupancySelector';

function NumberOfGuests() {
	const sectionTitleRef = useRef();

	const { updateSearchSelectingFilter, searchSelecting, resetSearchSelectingFilter, globalization, brand }: any = useContext(CruiseSearchContext);

	const { generalDictionary, filtersDictionary } = globalization?.dictionaries || {};
	const { labelFilterChildren_RCI, labelFilterChildren_CEL, labelNumberOfGuests, navNumberOfGuests } = filtersDictionary;

	const labelChildren = getLabelByBrand({
		brand,
		labelRCI: labelFilterChildren_RCI,
		labelCEL: labelFilterChildren_CEL,
	});

	const { labelTravelersFilterAdults, labelTravelersFilterKids } = generalDictionary;

	const [occupancyState, setOccupancyState] = useState<OccupancyState>({
		adults: { count: +searchSelecting?.adults || ADULTS_DEFAULT },
		children: { count: +searchSelecting?.children || CHILDREN_DEFAULT },
	});

	const updateOccupancyFilters = (option, value) => {
		updateOccupancyState(option, value);
		updateSearchSelectingFilter(option, Array.of(value));

		if (option === CHILDREN && !searchSelecting[ADULTS]) {
			updateSearchSelectingFilter(ADULTS, Array.of(occupancyState.adults.count));
		}
	};

	const resetOccupancyFilters = (option, value) => {
		updateOccupancyState(option, value);
		resetSearchSelectingFilter(option);
	};

	const updateOccupancyState = (option, value) => {
		setOccupancyState((prev) => {
			return {
				...prev,
				[option]: { count: value },
			};
		});
	};

	const isValidOccupancy = (option: string, count: number): boolean => {
		const { min, max } = OCCUPANCY_RANGE[option];
		return count >= min && count <= max && count > 0;
	};

	const validateTotalOccupancy = (totalGuests: number): boolean => {
		return totalGuests <= OCCUPANCY_RANGE.max;
	};

	const validateDisabledGroup = (option: string, count: number, button: string, isFull: boolean): boolean => {
		return isFull || OCCUPANCY_RANGE[option][button] === count;
	};

	const handleOccupancy = (option: string, value: number) => {
		const finalOccupancy = { ...occupancyState, [option]: { count: value } };

		if (isValidOccupancy(option, value)) {
			const totalOccupancy = Object.values(finalOccupancy).reduce((sum, occupancy) => sum + occupancy.count, 0);
			if (validateTotalOccupancy(totalOccupancy)) {
				updateOccupancyFilters(option, value);
			}
		} else {
			resetOccupancyFilters(option, value);
		}
	};

	const onReset = useCallback(() => {
		resetSearchSelectingFilter(ADULTS);
		resetSearchSelectingFilter(CHILDREN);
		setOccupancyState({ [ADULTS]: { count: ADULTS_DEFAULT }, [CHILDREN]: { count: CHILDREN_DEFAULT } });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		gsap.set(sectionTitleRef.current, {
			opacity: 0,
		});
		gsap.to(sectionTitleRef.current, {
			opacity: 1,
			duration: 0.25,
			ease: 'cubic-bezier(0,0,0,0)',
			stagger: 0.1,
		});
	}, []);

	const { disabledAdults, disabledChildren } = useMemo(() => {
		const adultsCount = occupancyState.adults.count || 0;
		const childrenCount = occupancyState.children.count || 0;
		const { max } = OCCUPANCY_RANGE;
		const isFull = adultsCount + childrenCount >= max;

		const disabledAdults = {
			increment: validateDisabledGroup(ADULTS, adultsCount, INCREMENT, isFull),
			decrement: validateDisabledGroup(ADULTS, adultsCount, DECREMENT, false),
		};
		const disabledChildren = {
			increment: validateDisabledGroup(CHILDREN, childrenCount, INCREMENT, isFull),
			decrement: validateDisabledGroup(CHILDREN, childrenCount, DECREMENT, false),
		};
		return {
			disabledAdults,
			disabledChildren,
		};
	}, [occupancyState]);

	return (
		<>
			<Header title={navNumberOfGuests} onReset={onReset} />
			<Constrain ref={sectionTitleRef}>
				<Grid>
					<Container>
						<Wrapper>
							<FilterTitle className="number-of-guests-adults">{labelTravelersFilterAdults}</FilterTitle>
						</Wrapper>
						<OccupancySelector group={ADULTS} handleOccupancy={handleOccupancy} disabledButton={disabledAdults} count={occupancyState.adults.count} />
					</Container>
					<Container>
						<Wrapper>
							<FilterTitle className="number-of-guests-children">{labelTravelersFilterKids}</FilterTitle>
							<Label>{labelChildren}</Label>
						</Wrapper>
						<OccupancySelector
							group={CHILDREN}
							handleOccupancy={handleOccupancy}
							disabledButton={disabledChildren}
							count={occupancyState.children.count}
						/>
					</Container>
				</Grid>
				<SectionLabel>{labelNumberOfGuests}</SectionLabel>
			</Constrain>
		</>
	);
}

export default NumberOfGuests;
